import { useCallback, useEffect, useRef, useState } from "react"
import { useElements, useStripe } from "@stripe/react-stripe-js"
import { captureMessage } from "@sentry/react"
import parse from "html-react-parser"

import { post } from "shared/lib/api"
import { rejectedXHRPromiseLogger } from "shared/lib/xhr_promise"

import classnames from "classnames"
import InlineErrors from "shared/components/inline_errors"
import Tooltip from "shared/components/tooltip"
import { gtag } from "shared/lib/utils"

import CreditCardField from "shared/components/credit_card_field"
import SubscriptionHelper from "./subscription_helper"
import PromoCodeForm from "./promo_code_form"

import scheduleAbandonedCheckoutEmail from "./abandoned_checkout_mail_helper"

import googlePayLogo from "images/shared/google_pay_logo.svg?raw"
import applePayLogo from "images/shared/apple_pay_logo.svg?raw"

import styles from "./payment_info_form.module.scss"

export interface URLs {
  createStripeCustomerUrl: string
  subscriptionsPath: string
  subscriptionSuccessPath: string
}

export interface PlanType {
  name: string
  amount: number
  interval: "month" | "year"
  local_price_formatted: string
  local_price_formatted_monthly: string
  local_price_monthly: number
  currency: string
}

interface Props {
  browserType: string
  eligibleForTrial: boolean
  email: string
  initialPromoCode: string | null
  modalButtonType: string
  monthlyPlan: PlanType
  onModalHandleSuccessfulSubscription: () => void
  planId: "intermediate_pro" | "pro"
  productId: string
  redirectToMobileAppOnSuccess: boolean
  referrerPath: string
  renderingInsideModal: boolean
  setPermissions?: (subscription: object) => void
  setSubscription?: (subscription: object) => void
  studentProgramCheckout?: boolean
  tier: string
  urls: URLs
  yearlyPlan: PlanType
  initialInterval?: "month" | "year"
  isCombinedForm?: boolean
  hidePromoCodeForm?: boolean
  holidayPromoActive?: boolean
}

export default function PaymentInfoForm({
  browserType,
  eligibleForTrial,
  email,
  initialPromoCode,
  hidePromoCodeForm,
  holidayPromoActive,
  isCombinedForm,
  modalButtonType,
  monthlyPlan,
  onModalHandleSuccessfulSubscription,
  planId,
  productId,
  redirectToMobileAppOnSuccess,
  referrerPath,
  renderingInsideModal,
  setPermissions,
  setSubscription,
  studentProgramCheckout,
  tier,
  urls,
  yearlyPlan,
  initialInterval,
}: Props) {
  const holidayPromoCode = window.App.holidayPromoCode
  // Holiday uses a custom workflow. We're not applying the change in the backend since it's temporary and we can't cache.
  const holidayYearPlan = {
    // The `amount` price will be based on the monthly price (without discount) to apply the 35% via promo code
    intermediate_pro: {
      amount: 4788,
      currency: "usd",
      interval: "year",
      local_price_formatted: "$31.08",
      local_price_formatted_monthly: "$2.59",
      local_price_monthly: 259,
    },
    pro: {
      amount: 14388,
      currency: "usd",
      interval: "year",
      local_price_formatted: "$93,48",
      local_price_formatted_monthly: "$7.79",
      local_price_monthly: 779,
    },
  }
  // Start holiday with the price details with the discount applied
  const holidayPromoPriceDetails = {
    intermediate_pro: {
      year_price: "$31.08",
      year_price_per_month: "$2.59",
    },
    pro: {
      year_price: "$93.48",
      year_price_per_month: "$7.79",
    },
  }

  const isSafari = browserType === "safari"
  const isChrome = browserType === "chrome"

  const stripe = useStripe()
  const elements = useElements()
  const [paymentRequest, setPaymentRequest] = useState(null)
  const [loadingPaymentRequest, setLoadingPaymentRequest] = useState(true)
  const [paymentRequestActive, setPaymentRequestActive] = useState(true)
  const [paymentRequestTypes, setPaymentRequestTypes] = useState<{ [key: string]: boolean }>({})

  const cardRef = useRef(null)
  const formRef = useRef(null)
  const [handlingSubmit, setHandlingSubmit] = useState(false)
  const [cardReady, setCardReady] = useState(false)
  const [promoCode, setPromoCode] = useState(holidayPromoActive ? holidayPromoCode[planId] : "")
  const [promoPriceDetails, setPromoPriceDetails] = useState(
    holidayPromoActive ? holidayPromoPriceDetails[planId] : null
  )
  const initialPlan = initialInterval === "month" ? monthlyPlan : holidayPromoActive ? holidayYearPlan[planId] : yearlyPlan
  const [plan, setPlan] = useState(initialPlan)

  const buttonTitle = eligibleForTrial && !studentProgramCheckout ? "Try it free" : "Upgrade"
  const submittingCopy = eligibleForTrial && !studentProgramCheckout ? "Submitting..." : "Upgrading..."

  const planName = "Pro"
  const priceTypeMonthly = "monthly"
  const priceTypeYearly = "yearly"
  const [selectedPrice, setSelectedPrice] = useState(initialPlan.amount)
  const [validationErrors, setValidationErrors] = useState(null)
  const discountForYearly = Math.round(((monthlyPlan.amount - yearlyPlan.amount / 12) / monthlyPlan.amount) * 100)

  const inlineErrorFields = ["price"]
  const defaultErrorMessagesForField = {
    price: ["Choose a price between $1.99 USD and $100 USD"],
  }

  useEffect(() => {
    // schedule the abandoned checkout worker once the page is viewed since
    // it can only be sent once, to non-pros
    scheduleAbandonedCheckoutEmail(tier)

    post(urls.createStripeCustomerUrl)
      .then(data => {
        const { errors } = data
        if (errors && errors.length > 0 && window.App.allowDebugErrorMessages) {
          captureMessage("createStripeCustomer failed", { extra: errors })
        }
      })
      .catch(rejectedXHRPromiseLogger())
  }, [])

  useEffect(() => {
    const price = getPrice()

    if (stripe) {
      if (!paymentRequest) {
        const pr = stripe.paymentRequest({
          country: "US",
          currency: price?.currency?.toLowerCase() || "usd",
          total: {
            label: planName,
            amount: selectedPrice,
            pending: true,
          },
          requestPayerName: true,
          requestPayerEmail: true,
        })

        pr.canMakePayment().then(result => {
          setLoadingPaymentRequest(false)

          if (result) {
            window.App.Utils?.logCustomDatadogAction("stripe_checkout_payment_request_available")
            setPaymentRequest(pr)
            setPaymentRequestTypes(result)
          } else {
            setTimeout(() => {
              setPaymentRequestActive(false)
            }, 1000)
          }
        })
      }
    }
  }, [stripe, paymentRequest])

  const setupPaymentMethodHandler = useCallback(
    (price: object) => {
      if (!paymentRequest) return
      if (isCombinedForm) {
        window.App.Utils?.logCustomDatadogAction("checkout_and_upsell_start")
      } else {
        window.App.Utils?.logCustomDatadogAction("stripe_checkout_start")
      }

      paymentRequest.on("cancel", async () => {
        setHandlingSubmit(false)
        if (isCombinedForm) {
          window.App.Utils?.logCustomDatadogAction("checkout_and_upsell_cancel")
        } else {
          window.App.Utils?.logCustomDatadogAction("stripe_checkout_cancel")
        }
      })

      // this creates a closure, so make sure to do it when the price is fixed
      paymentRequest.on("paymentmethod", async ev => {
        if (handlingSubmit) {
          return
        }
        setHandlingSubmit(true)

        await SubscriptionHelper({
          stripe,
          subscriptionsPath: urls.subscriptionsPath,
          price,
        }).subscribe(
          planId,
          productId,
          ev.paymentMethod,
          promoCode,
          (subscription: object, permissions: object) => {
            ev.complete("success")
            handleSuccessfulSubscription(subscription, permissions, planId, promoCode)
          },
          (error: object) => {
            ev.complete("fail")
            setPaymentRequest(null)
            handleError(error)
          }
        )
      })
    },
    [paymentRequest]
  )

  useEffect(() => {
    if (referrerPath && !referrerPath.match(/(user\/new|onboarding.*)/)) {
      sessionStorage.setItem("afterProPaymentPath", referrerPath)
    }

    if (studentProgramCheckout) handlePriceTypeChange(priceTypeYearly)
  }, [])

  function handlePaymentRequestButtonClick(event) {
    event.preventDefault()
    if (handlingSubmit) {
      return
    }
    setHandlingSubmit(true)

    const price = getPrice()

    paymentRequest?.update({
      total: {
        label: planName,
        amount: eligibleForTrial ? 0 : price.price_in_cents,
        currency: price.currency || "usd",
      },
    })
    paymentRequest?.show()
    setupPaymentMethodHandler(price)
  }

  async function handleSubmit(event: MouseEvent) {
    if (handlingSubmit) {
      return
    }
    setHandlingSubmit(true)

    // We don't want to let default form submission happen here,
    // which would refresh the page.
    event.preventDefault()

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded. Make sure to disable
      // form submission until Stripe.js has loaded.
      setHandlingSubmit(false)
      return
    }

    if (isCombinedForm) {
      window.App.Utils?.logCustomDatadogAction("checkout_and_upsell_start")
    } else {
      window.App.Utils?.logCustomDatadogAction("stripe_checkout_start")
    }

    await SubscriptionHelper({
      stripe,
      subscriptionsPath: urls.subscriptionsPath,
      price: getPrice(),
    }).subscribe(
      planId,
      productId,
      {
        card: elements.getElement(cardRef.current.getElement()),
        billing_details: { email },
      },
      promoCode,
      handleSuccessfulSubscription,
      handleError
    )
  }

  function onPageHandleSuccessfulSubscription(subscription: object, permissions: object) {
    let successUrl = urls.subscriptionSuccessPath + subscription.subscription_id
    if (redirectToMobileAppOnSuccess) {
      successUrl += "?mobile=1"
    }

    window.history.pushState({}, "Success! You now have access to IFTTT Pro", successUrl)

    setPermissions(permissions)
    setSubscription(subscription)
  }

  function handleSuccessfulSubscription(
    subscription: object,
    permissions: object,
    planId: string,
    promoCode: string | null
  ) {
    try {
      if (isCombinedForm) {
        window.App.Utils?.logCustomDatadogAction("checkout_and_upsell_success")
      } else {
        window.App.Utils?.logCustomDatadogAction("stripe_checkout_success")
      }

      // This event will fire 2 tags on Tag Manager: custom event and a purchase event. Populating datalayer with all necessary data.
      gtag("TM_payment_complete", {
        currency: plan.currency,
        eligible_for_trial: eligibleForTrial,
        plan_id: planId,
        plan_interval: subscription.payment_interval,
        price_in_cents: subscription.price_in_cents,
        promo_code: promoCode,
        subscription_id: subscription.subscription_id,
        items: [
          {
            item_id: subscription.product_id,
            item_name: planId,
            item_variant: subscription.payment_interval,
            eligible_for_trial: eligibleForTrial,
          },
        ],
      })
    } catch (e) {
      if (window.App.env && window.App.allowDebugErrorMessages) console.error(e)
    } finally {
      renderingInsideModal
        ? onModalHandleSuccessfulSubscription()
        : onPageHandleSuccessfulSubscription(subscription, permissions)
    }
  }

  function handleError(error) {
    setHandlingSubmit(false)
    const errorMessage = error.message || error

    if (error.attribute === "promo_code") {
      setPromoCode(null)
    }
    if (isCombinedForm) {
      window.App.Utils?.logCustomDatadogAction("checkout_and_upsell_failure", { error: errorMessage })
    } else {
      window.App.Utils?.logCustomDatadogAction("stripe_checkout_failure", { error: errorMessage })
    }

    if (!inlineErrorFields.includes(error.attribute)) {
      window.appendFlash(errorMessage, "danger")
      if (error?.code === "incomplete_number") {
        cardRef.current.scrollIntoView()
      }
      return
    }

    const errors = {}
    const defaultMessages = defaultErrorMessagesForField[error.attribute]
    errors[error.attribute] = defaultMessages || [errorMessage]
    setValidationErrors(errors)
    InlineErrors.scrollWithDelay()
  }

  function handlePriceTypeChange(priceType: string) {
    const plan = priceType === priceTypeYearly ? yearlyPlan : monthlyPlan
    setValidationErrors(null)

    if (holidayPromoActive) {
      if (priceType === priceTypeYearly) {
        setPromoCode(holidayPromoCode[planId])
        setPlan(holidayYearPlan[planId])
        setSelectedPrice(holidayYearPlan[planId].amount)
      } else {
        setPromoCode("")
        setPlan(plan)
        setSelectedPrice(plan.amount)
      }
    } else {
      setPlan(plan)
      setSelectedPrice(plan.amount)
    }
  }

  function openZendesk(e) {
    e.preventDefault()
    window.App.loadZendeskWidget()
  }

  function getPrice() {
    return {
      price_type: plan.interval === yearlyPlan.interval ? priceTypeYearly : priceTypeMonthly,
      price_in_cents: selectedPrice,
      currency: plan.currency,
    }
  }

  function submitButtonDisabled() {
    const formReady = cardReady && !handlingSubmit && !validationErrors
    return !formReady
  }

  const promoCodeApplied = holidayPromoActive ? false : promoCode && promoPriceDetails
  const appleOrGooglePaymentRequest = paymentRequest && (paymentRequestTypes.applePay || paymentRequestTypes.googlePay)

  return (
    <form ref={formRef} className={`form payment-info-form ${styles["payment-info-form"]}`} onSubmit={handleSubmit}>
      <div className={styles["stripe-payment-container"]}>
        <section className={styles["price-container"]}>
          {studentProgramCheckout ? (
            <div className={styles["price-annual-option"]}>
              <span className={styles["title"]}>Get started today</span>
              <span className={styles["price-value"]}>{yearlyPlan.local_price_formatted_monthly}/month</span>
              <span className={styles["price-period"]}>{`Billed at ${yearlyPlan.local_price_formatted} per year`}</span>
            </div>
          ) : holidayPromoActive ? (
            <>
              <div className={styles["price-option-labels"]}>
                <div>
                  Annual
                  <span className={styles["holiday-price-discount"]}>SAVE 35%</span>
                </div>
                <div>Monthly</div>
              </div>
              <div className={styles["price-list"]}>
                <div
                  className={classnames({
                    [styles["selected-price"]]: plan.interval === "year",
                  })}
                  role="button"
                  onClick={() => handlePriceTypeChange(priceTypeYearly)}
                >
                  <span className={`${styles["price-value"]} ${styles["full-price"]}`}>{yearlyPlan.local_price_formatted_monthly}</span>
                  <span className={styles["price-value"]}>{promoPriceDetails.year_price_per_month}</span>
                  <span className={styles["price-period"]}>/month</span>
                  <span className={classnames(styles["price-discount"], {
                    [styles["hidden"]]: true
                  })}>
                    SAVE {discountForYearly}%
                  </span>
                </div>
                <div
                  className={classnames({
                    [styles["selected-price"]]: plan.interval === "month",
                  })}
                  role="button"
                  onClick={() => handlePriceTypeChange(priceTypeMonthly)}
                >
                  <span className={styles["price-value"]}>{monthlyPlan.local_price_formatted}</span>
                  <span className={styles["price-period"]}>/month</span>
                </div>
              </div>
              <div className={`${styles["price-interval"]} mt1`}>
                <div className={classnames({ [styles["selected-interval"]]: plan.interval === "year" })}>
                  {promoPriceDetails.year_price} billed annually
                </div>
                <div className={classnames({ [styles["selected-interval"]]: plan.interval === "month" })}>Billed monthly</div>
              </div>
            </>
          ) : (
            <>
              <div className={styles["price-list"]}>
                <div
                  className={classnames({
                    [styles["selected-price"]]: plan.interval === "year",
                  })}
                  role="button"
                  onClick={() => handlePriceTypeChange(priceTypeYearly)}
                >
                  {promoCodeApplied ? (
                    <>
                      <span className={`${styles["price-value"]} ${styles["full-price"]}`}>{yearlyPlan.local_price_formatted_monthly}</span>
                      <span className={styles["price-value"]}>{promoPriceDetails.year_price_per_month}</span>
                    </>
                  ) : (
                    <span className={styles["price-value"]}>{yearlyPlan.local_price_formatted_monthly}</span>
                  )}
                  <span className={styles["price-period"]}>/month</span>
                  <span className={classnames(styles["price-discount"], {
                    [styles["hidden"]]: promoCodeApplied
                  })}>
                    SAVE {discountForYearly}%
                  </span>
                </div>
                <div
                  className={classnames({
                    [styles["selected-price"]]: plan.interval === "month",
                  })}
                  role="button"
                  onClick={() => handlePriceTypeChange(priceTypeMonthly)}
                >
                  {promoCodeApplied ? (
                    <>
                      <span className={`${styles["price-value"]} ${styles["full-price"]}`}>{monthlyPlan.local_price_formatted}</span>
                      <span className={styles["price-value"]}>{promoPriceDetails.month_price}</span>
                    </>
                  ) : (
                    <span className={styles["price-value"]}>{monthlyPlan.local_price_formatted}</span>
                  )}
                  <span className={styles["price-period"]}>/month</span>
                </div>
              </div>
              <div className={`${styles["price-interval"]} mt1`}>
                <div className={classnames({ [styles["selected-interval"]]: plan.interval === "year" })}>
                  {promoCodeApplied ? (
                    <>
                      <span className={styles["full-price"]}>{yearlyPlan.local_price_formatted} billed annually</span><br/>
                      {promoPriceDetails.year_price} billed annually
                    </>
                  ) : (
                    `${yearlyPlan.local_price_formatted} billed annually`
                  )}
                </div>
                <div className={classnames({ [styles["selected-interval"]]: plan.interval === "month" })}>Billed monthly</div>
              </div>
            </>
          )}
        </section>
        <section className="mt3">
          <CreditCardField ref={cardRef} onCardReadyChange={setCardReady} />

          <div className={classnames(styles["card-info"], { [styles.inactive]: !appleOrGooglePaymentRequest })}>
            <div className={styles["payment-request-separator"]}>
              <div className={styles["rule"]} />
              <div className={styles["copy"]}>or pay with</div>
              <div className={styles["rule"]} />
            </div>

            <div className={styles["payment-request-container"]}>
              <button
                onClick={handlePaymentRequestButtonClick}
                className={classnames(styles["payment-request"], { [styles.loading]: loadingPaymentRequest })}
                data-dd-action-name="Subscription wallet payment button"
              >
                <div className={styles["unavailable"]}>
                  {isSafari && !paymentRequestTypes.applePay && "Apple Pay not available"}
                  {isChrome && !paymentRequestTypes.googlePay && "GPay not available"}
                  {!isSafari && !isChrome && "Payment method not available"}
                </div>
                {appleOrGooglePaymentRequest && (
                  <>
                    {paymentRequestTypes.applePay && <div>{parse(applePayLogo)}</div>}
                    {paymentRequestTypes.googlePay && <div>{parse(googlePayLogo)}</div>}
                  </>
                )}
              </button>
            </div>

          </div>

          {!holidayPromoActive && (
            <PromoCodeForm
              initialPromoCode={initialPromoCode}
              isHidden={hidePromoCodeForm}
              onValidCodeApplied={setPromoCode}
              plans={{
                currency: plan.currency,
                monthlyPriceInCents: monthlyPlan.amount,
                selectedInterval: plan.interval,
                yearlyPriceInCents: yearlyPlan.amount,
              }}
              promoPriceDetails={promoPriceDetails}
              setPromoPriceDetails={setPromoPriceDetails}
            />
          )}
        </section>
      </div>
      <section className={classnames(`${styles["action-buttons"]} mt4 mt2--phone`, {
          [styles["modal-style"]]: renderingInsideModal,
          [styles["holiday-promo"]]: holidayPromoActive,
          [styles["holiday-ribbon"]]: holidayPromoActive && !isCombinedForm && plan.interval === yearlyPlan.interval,
        })}
      >
        <button
          id="submit-payment"
          type="submit"
          disabled={handlingSubmit}
          className={modalButtonType ? "button-secondary full-width" : "button-primary full-width"}
          title={buttonTitle}
          aria-label={buttonTitle}
          data-dd-action-name="Subscription credit card payment button"
        >
          {handlingSubmit ? submittingCopy : buttonTitle}
          {submitButtonDisabled() && !handlingSubmit && (
            <Tooltip>
              Please be sure you’ve correctly completed the form. <br />
              <a className="link-inline" onClick={openZendesk}>
                Contact us
              </a>
              for help.
            </Tooltip>
          )}
        </button>
      </section>
    </form>
  )
}
